import { useState } from "react";
import { Field } from "@/components/Field";
import { PageHead } from "@/components/PageHead";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { registry, type AllocResult } from "@/api/client";
import { StockPicker, type StockPick } from "@/modules/registry/StockPicker";
import { StatusChip, type StatusMap } from "@/components/StatusChip";
import { CsvUpload } from "@/components/CsvUpload";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { Select } from "@/components/inputs";
import { ALLOC_WHATS, parseAllocateCsvFor, RQ } from "@/modules/registry/parts_common";

const ACTION_CHIP: StatusMap = {
  // `deallocated` is gone: you never un-own a thing (REGISTRY.md §10).
  transferred: { label: "TRANSFERRED", color: "var(--ok)" },
  noop: { label: "ALREADY OWNED", color: "var(--t-mute)" },
  invalid: { label: "INVALID", color: "var(--bad)" },
  skipped: { label: "SKIPPED", color: "var(--t-mute)" },
};
const WHAT: Record<string, string> = { connection: "Connection", asset: "Asset", part: "Part" };

function chipLabel(r: AllocResult): string {
  if (r.action === "transferred") return `TRANSFERRED${r.deployment_seq ? ` · seq ${r.deployment_seq}` : ""}`;
  if (r.action === "noop") return "ALREADY OWNED";
  if (r.action === "skipped") return `SKIPPED — ${r.reason ?? ""}`;
  return `INVALID — ${r.reason ?? ""}`;
}

export function RegistryTransferPage() {
  const qc = useQueryClient();
  const companies = useQuery({ queryKey: [...RQ, "companies"], queryFn: registry.companies });
  const opts = companies.data?.companies ?? [];

  // single
  // A LIST, not one identifier: picking accumulates, and /transfer has always
  // taken a list. The CSV panel below is for lists that already exist.
  const [picked, setPicked] = useState<StockPick[]>([]);
  // WHO IS SELLING. Sent on every transfer; the server refuses the batch if
  // any identifier is not currently owned by it. Without it a mistyped
  // identifier silently moves someone else's equipment.
  const [fromCompany, setFromCompany] = useState("");
  const [company, setCompany] = useState("");
  // DESPATCH — one state PER FORM, not one for the page. They were shared,
  // on the reasoning that "despatch or not" is one question about a sale; it
  // is not. The two panels are independent operations, so ticking the single
  // transfer's box also ticked the bulk one, silently arming a despatch on a
  // CSV the operator had not looked at yet. State that is shared between two
  // forms is shared between two decisions.
  //
  // OFF by default. The server's own default is ship=True, on the reasoning
  // that the ordinary sale is sold-and-shipped — but a default that MOVES
  // GOODS is the wrong way round for a tick nobody looked at. D-82: ticking
  // OPENS A TRANSIT; the buyer accepts on arrival, so custody does not move
  // here either way.
  const [despatch, setDespatch] = useState(false);
  const [bulkDespatch, setBulkDespatch] = useState(false);
  const fb = useMutationFeedback();
  const single = useMutation({
    mutationFn: () => registry.transfer({ identifiers: picked.map((p) => p.id),
                                         from_company_id: fromCompany, company_id: company,
                                         ship: despatch }),
    onSuccess: () => { fb.ok(); setPicked([]); void qc.invalidateQueries({ queryKey: [...RQ] }); },
    onError: fb.fail,
  });
  const singleRes = single.data?.results?.[0];

  // bulk — a CSV list of IMEIs + a source and a destination company
  const [bulkFrom, setBulkFrom] = useState("");
  const [bulkCompany, setBulkCompany] = useState("");
  const [bulkWhat, setBulkWhat] = useState("connection");
  const [preview, setPreview] = useState<string[] | null>(null);
  const [rows, setRows] = useState<AllocResult[] | null>(null);
  const bulk = useMutation({
    mutationFn: async () => {
      if (!preview?.length) throw new Error("no IMEIs found in the file");
      return registry.transfer({ identifiers: preview,
                                from_company_id: bulkFrom, company_id: bulkCompany,
                                ship: bulkDespatch });
    },
    onSuccess: (r) => { setRows(r.results); void qc.invalidateQueries({ queryKey: [...RQ] }); },
  });

  // A FUNCTION, not a shared element: an element closes over one piece of
  // state, which is precisely how both forms ended up driving the same tick.
  const despatchTick = (checked: boolean, onChange: (v: boolean) => void) => (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5,
                    cursor: "pointer", color: "var(--t-body)" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      Also despatch to them — opens a transit; they confirm receipt on arrival
    </label>
  );

  return (
    <div className="mpage on">
      <PageHead eyebrow="Registry" title="Transfer & ship"
        desc="One door for everything: a connection (IMEI / board id), an asset (VIN / BIN: its installed parts and their connections follow) or a spare part (serial)." />

      <div className="section-label">Transfer from stock</div>
      <div className="panel panel-pad">
        {/* "↩ Return to inventory (deallocate)" was removed 2026-09-24. It sent
            the `__inventory__` sentinel as a transfer TARGET, and the sentinel
            stopped being a company at 0044: owner is NOT NULL and "in stock"
            became a STATE. Returning goods is a transfer BACK to the vendor,
            then set the state to `inventory` — two acts, two doors. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
          <Field label="Source — current owner" req>
            <Select className="fill" value={fromCompany}
                    onChange={(e) => { setFromCompany(e.target.value); setPicked([]); }}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Destination — new owner" req>
            <Select className="fill" value={company} onChange={(e) => setCompany(e.target.value)}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>

        <div style={{ marginTop: 14 }}>
          <Field label="What is moving — search the source's stock" req>
            <StockPicker company={fromCompany} selected={picked} onChange={setPicked} />
          </Field>
        </div>
        <div style={{ marginTop: 12 }}>
          {despatchTick(despatch, setDespatch)}
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center", justifyContent: "flex-end" }}>
          {fb.errorNode}
          {singleRes && <><span style={{ fontSize: 12, color: "var(--t-mute)" }}>{WHAT[singleRes.what ?? ""] ?? ""}{singleRes.parts_moved != null ? ` · ${singleRes.parts_moved} parts followed` : ""}</span><StatusChip st={singleRes.action} map={ACTION_CHIP}>{chipLabel(singleRes)}</StatusChip></>}
          <button className="btn teal"
                  disabled={!picked.length || !fromCompany || !company || fromCompany === company || single.isPending}
                  onClick={() => single.mutate()}>
            {picked.length > 1 ? `Transfer ${picked.length}` : "Transfer"}
          </button>
        </div>
      </div>

      <div className="section-label">Bulk allocate — one kind per sheet</div>
      <div className="panel panel-pad">
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
          <span style={{ fontSize: 12.5, color: "var(--t-mute)" }}>Sheet is for</span>
          <Select value={bulkWhat} onChange={(e) => { setBulkWhat(e.target.value); setPreview(null); setRows(null); }}>
            {ALLOC_WHATS.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
          </Select>
        </div>
        {/* The SAME two labelled fields the single form uses, in the same
            order, rather than bare dropdowns tucked beside the button: the
            sentence is "from X, these rows, to Y" on both forms. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
                      gap: 14, marginBottom: 14 }}>
          <Field label="Source — current owner" req>
            <Select className="fill" value={bulkFrom} onChange={(e) => setBulkFrom(e.target.value)}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Destination — new owner" req>
            <Select className="fill" value={bulkCompany} onChange={(e) => setBulkCompany(e.target.value)}>
              <option value="">select…</option>
              {opts.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Template">
            <a className="btn ghost sm" href={registry.transferTemplateUrl(bulkWhat)}
               style={{ display: "inline-flex", alignItems: "center" }}>⭳ Download .csv</a>
          </Field>
        </div>
        <div style={{ marginBottom: 14 }}>{despatchTick(bulkDespatch, setBulkDespatch)}</div>
        <CsvUpload<string> key={bulkWhat}
          templateUrl={registry.transferTemplateUrl(bulkWhat)}
          parse={parseAllocateCsvFor(bulkWhat)}
          onParsed={(items) => { setPreview(items); setRows(null); }}
          onSubmit={() => bulk.mutate()}
          actionLabel={(n) => (n ? `Transfer ${n}` : "Transfer")}
          disabled={!bulkFrom || !bulkCompany || bulkFrom === bulkCompany}
          busy={bulk.isPending}
          error={bulk.isError ? <span style={{ color: "var(--bad)", fontSize: 12 }}>{String(bulk.error)}</span> : undefined}
        />
        {rows ? (
          <div className="tblwrap" style={{ marginTop: 14 }}>
            <table className="tbl">
              <thead><tr><th>Identifier</th><th>What</th><th>Current</th><th>Action</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td className="mono">{r.identifier ?? r.imei}</td>
                    <td>{WHAT[r.what ?? ""] ?? "—"}{r.parts_moved != null ? <span className="muted"> · {r.parts_moved} parts</span> : null}</td>
                    <td>{r.current ?? <span style={{ color: "var(--t-dim)" }}>Inventory</span>}</td>
                    <td><StatusChip st={r.action} map={ACTION_CHIP}>{chipLabel(r)}</StatusChip></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : preview && preview.length > 0 ? (
          <div className="tblwrap" style={{ marginTop: 14 }}>
            <table className="tbl">
              <thead><tr><th>Identifier</th><th>Status</th></tr></thead>
              <tbody>
                {preview.map((im, i) => (
                  <tr key={i}><td className="mono">{im}</td><td><span className="chip mute">READY</span></td></tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: "var(--t-dim)", marginTop: 8 }}>{preview.length} identifier{preview.length === 1 ? "" : "s"} parsed — pick a company, then Allocate.</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
